import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  tenantId?: string;
  avatarUrl?: string | null;
  phone?: string;
  bio?: string;
  timezone?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await api.post('/auth/admin/login', { email, password });
        const { admin, accessToken, refreshToken } = response.data;

        // Store refresh token
        localStorage.setItem('refreshToken', refreshToken);

        set({
          user: {
            id: admin.id,
            name: admin.name,
            email: admin.email,
            role: admin.role,
            tenantId: admin.tenantId,
            avatarUrl: admin.avatarUrl,
          },
          accessToken,
          isAuthenticated: true,
        });
      },

      logout: () => {
        api.post('/auth/logout').catch(() => {});
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
        });
      },

      refreshAuth: async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          localStorage.setItem('refreshToken', newRefreshToken);
          set({ accessToken });
        } catch {
          get().logout();
        }
      },

      updateUser: (data: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...data } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
